import React, { useState, useMemo } from 'react';
import { Home, HomeIcon, Utensils, Zap, Shield } from 'lucide-react';
import { Card } from '../ui';
import { Input } from '../ui';

export function ChapterHouseRentModeler() {
  const [liveInCapacity, setLiveInCapacity] = useState<number>(40);
  const [outOfHouseMembers, setOutOfHouseMembers] = useState<number>(60);
  
  const [mortgage, setMortgage] = useState<number>(120000);
  const [insurance, setInsurance] = useState<number>(25000);
  const [maintenance, setMaintenance] = useState<number>(15000);
  
  const [utilities, setUtilities] = useState<number>(35000);
  const [mealPlan, setMealPlan] = useState<number>(85000);
  
  const [parlorFee, setParlorFee] = useState<number>(250); // Fee charged to out-of-house members per semester

  const model = useMemo(() => {
    const fixedCosts = mortgage + insurance + maintenance;
    const variableCosts = utilities + mealPlan;
    const totalAnnualCosts = fixedCosts + variableCosts;

    // Revenue from out of house members (Parlor fee * 2 semesters * members)
    const outOfHouseRevenue = outOfHouseMembers * parlorFee * 2;
    
    // Remaining cost that must be covered by live-in rent
    const requiredLiveInRevenue = Math.max(0, totalAnnualCosts - outOfHouseRevenue);
    
    // Annual rent per live-in member
    const annualRentPerMember = liveInCapacity > 0 ? requiredLiveInRevenue / liveInCapacity : 0;
    
    // Semester breakdown
    const semesterRentPerMember = annualRentPerMember / 2;
    const monthlyRentPerMember = annualRentPerMember / 9; // Assuming 9 month academic year

    return {
      fixedCosts,
      variableCosts,
      totalAnnualCosts,
      outOfHouseRevenue,
      requiredLiveInRevenue,
      annualRentPerMember,
      semesterRentPerMember,
      monthlyRentPerMember
    };
  }, [liveInCapacity, outOfHouseMembers, mortgage, insurance, maintenance, utilities, mealPlan, parlorFee]);

  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-cyan-700 to-blue-900 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <HomeIcon className="w-6 h-6 mr-3 text-cyan-300" />
            Chapter House Rent Modeler
          </h2>
          <p className="text-cyan-100 max-w-xl">
            Model your chapter house operating budget. Calculate the exact break-even rent you need to charge live-in members by subsidizing overhead with out-of-house parlor fees.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        
        {/* Controls */}
        <div className="space-y-6">
          <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2 border-b border-slate-100 dark:border-slate-800 pb-2">Occupancy</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Live-In Capacity</label>
                <Input 
                  type="number" 
                  value={liveInCapacity} 
                  onChange={(e) => setLiveInCapacity(Number(e.target.value))}
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Out-of-House Members</label>
                <Input 
                  type="number" 
                  value={outOfHouseMembers} 
                  onChange={(e) => setOutOfHouseMembers(Number(e.target.value))}
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Parlor Fee (Per Semester) ($)</label>
                <Input 
                  type="number" 
                  value={parlorFee} 
                  onChange={(e) => setParlorFee(Number(e.target.value))}
                />
                <p className="text-xs text-slate-400 mt-1">Charged to out-of-house members to subsidize common areas.</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2 border-b border-slate-100 dark:border-slate-800 pb-2">Annual Fixed Costs</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-slate-500 flex items-center mb-2">
                  <Home className="w-4 h-4 mr-2" /> Mortgage / Lease
                </label>
                <Input type="number" value={mortgage} onChange={(e) => setMortgage(Number(e.target.value))} />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-500 flex items-center mb-2">
                  <Shield className="w-4 h-4 mr-2" /> Liability & Property Insurance
                </label>
                <Input type="number" value={insurance} onChange={(e) => setInsurance(Number(e.target.value))} />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-500 flex items-center mb-2">
                  <Zap className="w-4 h-4 mr-2" /> Maintenance Fund
                </label>
                <Input type="number" value={maintenance} onChange={(e) => setMaintenance(Number(e.target.value))} />
              </div>
            </div>
          </Card>

          <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2 border-b border-slate-100 dark:border-slate-800 pb-2">Annual Variable Costs</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-slate-500 flex items-center mb-2">
                  <Zap className="w-4 h-4 mr-2" /> Utilities (Water, Power, Trash)
                </label>
                <Input type="number" value={utilities} onChange={(e) => setUtilities(Number(e.target.value))} />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-500 flex items-center mb-2">
                  <Utensils className="w-4 h-4 mr-2" /> Chef & Meal Plan
                </label>
                <Input type="number" value={mealPlan} onChange={(e) => setMealPlan(Number(e.target.value))} />
              </div>
            </div>
          </Card>
        </div>

        {/* Results */}
        <div className="space-y-6">
          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-cyan-900 text-white flex flex-col justify-center min-h-[250px]">
            <h3 className="text-sm font-bold text-cyan-300 uppercase tracking-wider mb-6 border-b border-cyan-800 pb-2">Required Live-In Rent</h3>
            
            <div className="space-y-6">
              <div>
                <div className="text-sm text-cyan-400 font-semibold mb-1">Per Semester (Billed 2x)</div>
                <div className="text-6xl font-black text-white tracking-tight">
                  {formatCurrency(model.semesterRentPerMember)}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-cyan-950/50 p-3 rounded-lg">
                  <div className="text-xs text-cyan-500 font-bold uppercase tracking-wider">Per Month (9-Mo)</div>
                  <div className="text-2xl font-bold">{formatCurrency(model.monthlyRentPerMember)}</div>
                </div>
                <div className="bg-cyan-950/50 p-3 rounded-lg">
                  <div className="text-xs text-cyan-500 font-bold uppercase tracking-wider">Annual Total</div>
                  <div className="text-2xl font-bold">{formatCurrency(model.annualRentPerMember)}</div>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-0 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 overflow-hidden">
            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 border-b border-slate-100 dark:border-slate-800">
              <h3 className="font-bold">Operating Budget Breakdown</h3>
            </div>
            
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              <div className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Total Annual Costs</div>
                  <div className="text-sm text-slate-500">Fixed + Variable overhead</div>
                </div>
                <div className="text-xl font-bold text-red-600 dark:text-red-400">
                  {formatCurrency(model.totalAnnualCosts)}
                </div>
              </div>
              
              <div className="p-4 flex items-center justify-between bg-emerald-50 dark:bg-emerald-900/10">
                <div>
                  <div className="font-bold text-emerald-800 dark:text-emerald-300">Parlor Fee Subsidy</div>
                  <div className="text-sm text-emerald-600 dark:text-emerald-400">From {outOfHouseMembers} out-of-house members</div>
                </div>
                <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                  -{formatCurrency(model.outOfHouseRevenue)}
                </div>
              </div>
              
              <div className="p-4 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Net Revenue Target</div>
                  <div className="text-sm text-slate-500">To be covered by {liveInCapacity} live-ins</div>
                </div>
                <div className="text-2xl font-black text-slate-900 dark:text-white">
                  {formatCurrency(model.requiredLiveInRevenue)}
                </div>
              </div>
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}
